import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useRestoreBackupApi } from '../api/use-backup-restore-budget';
// Backup Restore Form Schema
export const backupRestoreFormSchema = z.object({
    backupId: z.string(),
    name: z.string().min(1, 'Budget name is required'),
});
// Backup Restore Form Hook
export const useBackupRestoreForm = ({ backups, budget, budgets, onSuccess }) => {
    const { mutate: restoreBackup, isPending } = useRestoreBackupApi({ onSuccess });
    // enhance schema with name uniqueness validation
    const schema = backupRestoreFormSchema.refine((data) => {
        const isNameTaken = budgets.some((b) => b.name.toLowerCase() === data.name.toLowerCase());
        return !isNameTaken;
    }, {
        message: 'A budget with this name already exists',
        path: ['newName'],
    });
    const defaultValues = useMemo(() => toFormData(backups, budget.name), [backups, budget.name]);
    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    });
    useEffect(() => {
        form.reset(defaultValues);
    }, [backups, budget, form, defaultValues]);
    /**
     * Handles the form submission event, building the API payload and
     * invoking the restore mutation.
     *
     * @param values - Sanitized backup restore form values.
     */
    const submitForm = useCallback((values) => {
        const payload = toRequestData(values);
        restoreBackup(payload);
    }, [restoreBackup]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isPending,
        backups,
    };
};
/**
 * Compute the initial values for the BackupRestoreForm.
 *
 * @param backups - The list of available backups
 * @param budgetName - The name of the current budget
 * @returns A fully populated BackupRestoreForm
 */
function toFormData(backups, budgetName) {
    const firstBackup = backups[0];
    return {
        backupId: firstBackup?.id || '',
        name: `${budgetName} (Restored)`,
    };
}
/**
 * Build the BackupRestoreRequest payload from form values and context.
 *
 * @param formValues - The values from the backup restore form.
 * @returns A fully populated BackupRestoreRequest for the API.
 */
function toRequestData(formValues) {
    return {
        backupId: formValues.backupId,
        name: formValues.name,
    };
}
